import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, CheckCircle2, RotateCcw, BellRing, Link2, Repeat, Pencil, Send, Mail, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { fmt } from "@/lib/format";
import { StatusBadge } from "@/components/common";
import { InvoiceDocument } from "@/components/InvoiceDocument";
import { CreditNotes } from "@/components/CreditNotes";
import { PdfButton } from "@/components/PdfButton";
import { FxPaymentDialog } from "@/components/FxPaymentDialog";
import { Button } from "@/components/ui/button";

export default function InvoiceView() {
  const { id } = useParams();
  const { t, locale } = useLang();
  const [inv, setInv] = useState(null);
  const [co, setCo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fxOpen, setFxOpen] = useState(false);
  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data));
    api.get("/company").then((r) => setCo(r.data));
  }, [id]);
  if (!inv || !co) return <div className="text-slate-400">{t("loading")}</div>;

  const toggle = async () => {
    if (inv.status !== "paid" && inv.currency && inv.currency !== "AED") { setFxOpen(true); return; }
    try {
      const { data } = await api.patch(`/invoices/${id}/status`, { status: inv.status === "paid" ? "unpaid" : "paid" });
      setInv(data);
      toast.success(t("saved"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sendMail = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/send`);
      setInv(data);
      toast.success(t("invoiceEmailed"));
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "client_no_email" ? t("clientNoEmail") : code === "email_failed" ? t("emailFailed") : errMsg(e));
    }
    setBusy(false);
  };
  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/${id}/remind`);
      setInv(data);
      toast.success(t("reminderSent"));
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      toast.error(code === "client_no_email" ? t("clientNoEmail") : code === "email_failed" ? t("emailFailed") : errMsg(e));
    }
    setBusy(false);
  };
  const share = async () => {
    try {
      const { data } = await api.post(`/invoices/${id}/share-link`);
      await navigator.clipboard.writeText(data.url);
      toast.success(t("linkCopied"));
    } catch (e) { toast.error(errMsg(e)); }
  };
  const recur = async () => {
    const day = window.prompt(t("dayOfMonthPrompt"), "1");
    if (!day) return;
    try {
      await api.post("/recurring", { invoice_id: id, day_of_month: Number(day) });
      toast.success(t("recurringCreated"), { action: { label: t("recurring"), onClick: () => (window.location.href = "/recurring") } });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const overdue = inv.status !== "paid" && inv.due_date < new Date().toISOString().slice(0, 10);
  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/invoices/${id}/status`, { status: "unpaid" });
      setInv(data);
      toast.success(t("invoiceIssued"));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  if (inv.status === "draft")
    return (
      <div data-testid="invoice-view-page">
        <div className="no-print mb-6 flex flex-wrap items-center gap-2">
          <Button asChild variant="ghost" data-testid="invoice-back-btn"><Link to="/invoices"><ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />{t("back")}</Link></Button>
          <StatusBadge status="draft" />
          <span data-testid="invoice-draft-hint" className="text-xs text-slate-500">{t("draftHint")}</span>
          <div className="ms-auto flex flex-wrap gap-2">
            <Button asChild variant="outline" data-testid="invoice-edit-btn"><Link to={`/invoices/${id}/edit`}><Pencil className="me-1 h-4 w-4" />{t("edit")}</Link></Button>
            <Button data-testid="invoice-issue-btn" disabled={busy} onClick={issue} className="bg-[#007A3D] hover:bg-[#005E2E]"><Send className="me-1 h-4 w-4" />{t("issueInvoice")}</Button>
          </div>
        </div>
        <InvoiceDocument inv={inv} co={co} />
      </div>
    );

  return (
    <div data-testid="invoice-view-page">
      <div className="no-print mb-6 flex flex-wrap items-center gap-2">
        <Button asChild variant="ghost" data-testid="invoice-back-btn"><Link to="/invoices"><ArrowLeft className="me-1 h-4 w-4 rtl:-scale-x-100" />{t("back")}</Link></Button>
        <StatusBadge status={inv.status} />
        {inv.reminder_count > 0 && <span data-testid="invoice-reminder-info" className="text-xs text-slate-500">{t("lastReminder")} {new Date(inv.last_reminder_at).toLocaleDateString(locale)} ({inv.reminder_count})</span>}
        {inv.sent_at && <span data-testid="invoice-sent-info" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800 ring-1 ring-emerald-200"><Mail className="h-3 w-3" />{t("sentOn")} {new Date(inv.sent_at).toLocaleDateString(locale)}{inv.sent_count > 1 && ` (${inv.sent_count})`}</span>}
        {inv.fx_diff != null && <span data-testid="invoice-fx-diff" className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${inv.fx_diff >= 0 ? "bg-emerald-50 text-emerald-800 ring-emerald-200" : "bg-red-50 text-[#A50E1F] ring-red-200"}`}>{inv.fx_diff >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{t(inv.fx_diff >= 0 ? "fxGain" : "fxLoss")} {inv.fx_diff >= 0 ? "+" : ""}{fmt(inv.fx_diff)} AED · 1 {inv.currency} = {inv.payment_fx_rate}</span>}
        <div className="ms-auto flex flex-wrap gap-2">
          <CreditNotes invoice={inv} />
          <Button variant="outline" data-testid="invoice-send-btn" disabled={busy} onClick={sendMail}><Mail className="me-1 h-4 w-4" />{t("sendInvoiceEmail")}</Button>
          {inv.status !== "paid" && (
            <Button variant="outline" data-testid="invoice-remind-btn" disabled={busy} onClick={remind} className={overdue ? "border-[#CE1126]/40 text-[#A50E1F] hover:text-[#A50E1F]" : ""}>
              <BellRing className="me-1 h-4 w-4" />{t("sendReminder")}
            </Button>
          )}
          <Button variant="outline" data-testid="invoice-recurring-btn" onClick={recur}><Repeat className="me-1 h-4 w-4" />{t("makeRecurring")}</Button>
          <Button variant="outline" data-testid="invoice-share-btn" onClick={share}><Link2 className="me-1 h-4 w-4" />{t("copyLink")}</Button>
          <Button variant="outline" data-testid="invoice-toggle-paid-btn" onClick={toggle}>
            {inv.status === "paid" ? <RotateCcw className="me-1 h-4 w-4" /> : <CheckCircle2 className="me-1 h-4 w-4" />}{t(inv.status === "paid" ? "markUnpaid" : "markPaid")}
          </Button>
          <PdfButton kind="invoice" id={id} name={inv.number} testid="invoice-pdf-btn" />
          <Button data-testid="invoice-print-btn" onClick={() => window.print()} className="bg-[#0B1220] hover:bg-slate-800"><Printer className="me-1 h-4 w-4" />{t("print")}</Button>
        </div>
      </div>
      <InvoiceDocument inv={inv} co={co} />
      {fxOpen && <FxPaymentDialog inv={inv} open={fxOpen} onClose={() => setFxOpen(false)} onPaid={(d) => { setInv(d); setFxOpen(false); }} />}
    </div>
  );
}
